'use client';

import { TechnologyOption } from '@/lib/tech-stack-types';
import { prepareRadarData, RadarQuadrant, RadarTechnology } from '@/lib/visualization-data';
import { useState, useMemo, useCallback } from 'react';

interface TechStackRadarProps {
  technologies: TechnologyOption[];
  size?: number;
}

const RING_COLORS = {
  adopt: '#10B981', // Green
  trial: '#3B82F6', // Blue
  assess: '#F59E0B', // Yellow
  hold: '#EF4444', // Red
};

const RING_LABELS = {
  adopt: 'Adopt',
  trial: 'Trial',
  assess: 'Assess',
  hold: 'Hold',
};

const RING_DESCRIPTIONS = {
  adopt: 'Mature, recommended for production use',
  trial: 'Stable, try in non-critical projects',
  assess: 'Emerging, watch and experiment',
  hold: 'Declining or problematic, avoid',
};

export default function TechStackRadar({ technologies, size = 800 }: TechStackRadarProps) {
  const [selectedTech, setSelectedTech] = useState<RadarTechnology | null>(null);
  const [selectedQuadrant, setSelectedQuadrant] = useState<string | null>(null);
  const [hoveredTechId, setHoveredTechId] = useState<string | null>(null);

  // Memoize radar data calculation
  const radarData = useMemo(() => prepareRadarData(technologies), [technologies]);
  const center = size / 2;
  const radius = size / 2 - 60;

  // Memoize visible technologies
  const visibleTechnologies = useMemo(() =>
    selectedQuadrant
      ? radarData.filter(q => q.name === selectedQuadrant).flatMap(q => q.technologies)
      : radarData.flatMap(q => q.technologies),
    [radarData, selectedQuadrant]
  );

  // Memoize event handlers to prevent re-creation
  const handleMouseEnter = useCallback((techId: string) => {
    setHoveredTechId(techId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredTechId(null);
  }, []);

  const handleClick = useCallback((tech: RadarTechnology) => {
    setSelectedTech(prev => prev?.id === tech.id ? null : tech);
  }, []);

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Technology Radar</h3>
        <p className="text-foreground-secondary text-sm">
          Explore the technology landscape across four quadrants. Each technology is positioned based on its maturity and adoption status.
        </p>
      </div>

      {/* Quadrant Filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedQuadrant(null)}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            selectedQuadrant === null
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground-secondary hover:bg-surface/80'
          }`}
        >
          All Quadrants
        </button>
        {radarData.map(quadrant => (
          <button
            key={quadrant.name}
            onClick={() => setSelectedQuadrant(quadrant.name)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedQuadrant === quadrant.name
                ? 'bg-accent text-accent-foreground'
                : 'bg-surface text-foreground-secondary hover:bg-surface/80'
            }`}
          >
            {quadrant.name}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Radar Chart */}
        <div className="lg:col-span-2 bg-surface rounded-lg p-6">
          <svg
            width="100%"
            height="100%"
            viewBox={`0 0 ${size} ${size}`}
            className="max-w-full h-auto"
          >
            {/* Background circles for rings */}
            {[1, 0.75, 0.5, 0.25].map((r, index) => {
              const ringNames = ['hold', 'assess', 'trial', 'adopt'] as const;
              const ringName = ringNames[index];
              return (
                <g key={r}>
                  <circle
                    cx={center}
                    cy={center}
                    r={radius * r}
                    fill="none"
                    stroke="#E8D5B7"
                    strokeOpacity={0.2}
                    strokeWidth={1}
                  />
                  {/* Ring label */}
                  {index < 3 && (
                    <text
                      x={center}
                      y={center - radius * r - 5}
                    textAnchor="middle"
                      fill="#E8D5B7"
                      fontSize="12"
                      opacity={0.8}
                    >
                      {RING_LABELS[ringName]}
                    </text>
                  )}
                </g>
              );
            })}

            {/* Quadrant dividers */}
            <line
              x1={center}
              y1={60}
              x2={center}
              y2={size - 60}
              stroke="#E8D5B7"
              strokeOpacity={0.2}
              strokeWidth={1}
            />
            <line
              x1={60}
              y1={center}
              x2={size - 60}
              y2={center}
              stroke="#E8D5B7"
              strokeOpacity={0.2}
              strokeWidth={1}
            />

            {/* Quadrant labels */}
            {radarData.map((quadrant, index) => {
              const angle = index * 90 + 45;
              const radians = (angle - 90) * (Math.PI / 180);
              const labelRadius = radius + 30;
              const x = center + labelRadius * Math.cos(radians);
              const y = center + labelRadius * Math.sin(radians);

              return (
                <text
                  key={quadrant.name}
                  x={x}
                  y={y}
                  textAnchor="middle"
                  fill="#E8D5B7"
                  fontSize="14"
                  fontWeight="600"
                  opacity={selectedQuadrant === null || selectedQuadrant === quadrant.name ? 1 : 0.3}
                >
                  {quadrant.name.split(' ').map((word, i) => (
                    <tspan key={i} x={x} dy={i === 0 ? 0 : 16}>
                      {word}
                    </tspan>
                  ))}
                </text>
              );
            })}

            {/* Technology dots */}
            {visibleTechnologies.map(tech => {
              const x = center + tech.x * radius;
              const y = center + tech.y * radius;
              const isSelected = selectedTech?.id === tech.id;
              const isHovered = hoveredTechId === tech.id;

              return (
                <g
                  key={tech.id}
                  onMouseEnter={() => handleMouseEnter(tech.id)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => handleClick(tech)}
                  className="cursor-pointer"
                >
                  {/* Larger invisible hit area */}
                  <circle
                    cx={x}
                    cy={y}
                    r={12}
                    fill="transparent"
                    pointerEvents="all"
                  />
                  {/* Visible dot */}
                  <circle
                    cx={x}
                    cy={y}
                    r={isSelected ? 8 : isHovered ? 7 : 6}
                    fill={RING_COLORS[tech.ring]}
                    stroke={isSelected ? '#E8D5B7' : isHovered ? '#E8D5B7' : 'none'}
                    strokeWidth={isSelected ? 2 : 1}
                    opacity={isSelected ? 1 : isHovered ? 0.9 : 0.8}
                    pointerEvents="none"
                  />
                  {/* Technology label on hover/select */}
                  {(isSelected || isHovered) && (
                    <text
                      x={x}
                      y={y - 15}
                      textAnchor="middle"
                      fill="#E8D5B7"
                      fontSize="11"
                      fontWeight="600"
                      pointerEvents="none"
                    >
                      {tech.name}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Legend and Selected Technology Info */}
        <div className="space-y-6">
          {/* Ring Legend */}
          <div className="bg-surface rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground mb-3">Adoption Rings</h4>
            <div className="space-y-3">
              {Object.entries(RING_LABELS).map(([key, label]) => (
                <div key={key} className="flex items-start gap-3">
                  <div
                    className="w-4 h-4 rounded-full mt-0.5 flex-shrink-0"
                    style={{ backgroundColor: RING_COLORS[key as keyof typeof RING_COLORS] }}
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">{label}</div>
                    <div className="text-xs text-foreground-tertiary">
                      {RING_DESCRIPTIONS[key as keyof typeof RING_DESCRIPTIONS]}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Selected Technology Details */}
          {selectedTech && (
            <div className="bg-surface rounded-lg p-4 border-2 border-accent">
              <div className="flex items-start justify-between mb-2">
                <h4 className="text-lg font-semibold text-foreground">{selectedTech.name}</h4>
                <button
                  onClick={() => setSelectedTech(null)}
                  className="text-foreground-tertiary hover:text-foreground text-xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-xs font-semibold text-foreground-secondary mb-1">Description</div>
                  <div className="text-sm text-foreground/90">{selectedTech.description}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-xs font-semibold text-foreground-secondary mb-1">Ring</div>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: RING_COLORS[selectedTech.ring] }}
                      />
                      <span className="text-sm text-foreground">{RING_LABELS[selectedTech.ring]}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-foreground-secondary mb-1">Maturity</div>
                    <div className="text-sm text-foreground capitalize">{selectedTech.maturity}</div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-foreground-secondary mb-1">Popularity Trend</div>
                  <div className="text-sm text-foreground capitalize">
                    {selectedTech.popularityTrend.replace('-', ' ')}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-foreground-secondary mb-1">Category</div>
                  <div className="text-sm text-foreground capitalize">
                    {selectedTech.category.split('-').join(' ')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Technology Count */}
          <div className="bg-surface rounded-lg p-4">
            <div className="text-sm text-foreground-secondary">
              Showing <span className="font-semibold text-foreground">{visibleTechnologies.length}</span> technologies
              {selectedQuadrant && (
                <span> in <span className="font-semibold text-foreground">{selectedQuadrant}</span></span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-friendly technology list */}
      <div className="lg:hidden bg-surface rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-3">Technologies by Ring</h4>
        {Object.entries(RING_LABELS).map(([ring, label]) => {
          const techs = visibleTechnologies.filter(t => t.ring === ring);
          if (techs.length === 0) return null;

          return (
            <div key={ring} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: RING_COLORS[ring as keyof typeof RING_COLORS] }}
                />
                <span className="text-sm font-semibold text-foreground">{label}</span>
                <span className="text-xs text-foreground-tertiary">({techs.length})</span>
              </div>
              <div className="flex flex-wrap gap-2 ml-5">
                {techs.map(tech => (
                  <button
                    key={tech.id}
                    onClick={() => setSelectedTech(tech)}
                    className="px-2 py-1 bg-background rounded text-xs text-foreground-secondary hover:text-foreground hover:bg-background/80"
                  >
                    {tech.name}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
